import React, { useCallback, useEffect, useState } from 'react';
import { useObservableState } from 'observable-hooks';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { route } from '../../../services/utils';
import { useService } from '../../../services/services';
import Form, { SubmitType } from '../../../components/form';
import Group from '../../../components/form/elements/group';
import Text from '../../../components/form/elements/text';
import Checkbox from '../../../components/form/elements/checkbox';
import Link from '../../../components/form/elements/link';
import Button from '../../../components/form/elements/button';
import { FARM_USERS_VIEW } from '../../../router/routes';
import Abilities, { AbilityGroup } from './abilities';
import { FarmUser } from '../../../interfaces/farm-user';
import TextArea from '../../../components/form/elements/text-area';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { map } from 'rxjs/operators';
import Readonly from '../../../components/form/elements/readonly';
import Show from '../../../components/form/elements/show';

export const userSchema = yup.object().shape({
  owner: yup.boolean(),
  main_user: yup.boolean(),
  blocked: yup.boolean(),
  comment: yup.string().nullable(),
});

export default function FarmEditUser() {
  const { id, farmId } = useParams<{ farmId: string; id: string }>();
  const { authenticatedUser, selectedFarm } = useSelector(
    (state: RootState) => ({
      selectedFarm: state.global.farm,
      authenticatedUser: state.global.user,
    })
  );
  const { t } = useTranslation();
  const { farmUserService } = useService();
  const history = useHistory();
  const [bodyElements, setBodyElements] = useState([]);
  const [footerElements, setFooterElements] = useState([]);
  const [farmUser] = useObservableState<FarmUser>(
    () =>
      farmUserService
        .show(farmId, id)
        .pipe(
          map((farmUser) => ({ ...farmUser, farm_name: selectedFarm.name }))
        ),
    null
  );
  const [abilities] = useObservableState<AbilityGroup[]>(
    () => farmUserService.abilities(farmId),
    null
  );
  const [selectedAbilities, setSelectedAbilities] = useState({});

  useEffect(() => {
    if (farmUser !== null && abilities) {
      const editingSelf = authenticatedUser.id === farmUser.user_id;
      setBodyElements([
        Group([
          Readonly([
            Text('farm_name', t('pages.farm.users.farm_name')),
            Text('name', t('pages.farm.users.name')),
            Text('email', t('pages.farm.users.email')),
            Checkbox('owner', t('pages.farm.users.owner')),
          ]),
          ...(farmUser.owner || (farmUser.main_user && editingSelf)
            ? [
                Readonly([
                  Checkbox('main_user', t('pages.farm.users.main_user')),
                  Checkbox('blocked', t('pages.farm.users.blocked')),
                ]),
              ]
            : [
                Checkbox('main_user', t('pages.farm.users.main_user')),
                Checkbox('blocked', t('pages.farm.users.blocked')),
              ]),
          TextArea('comment', t('pages.farm.users.comment')),
          Show(
            Abilities(
              abilities,
              Object.fromEntries(
                farmUser.abilities.map((value) => [value.id, true])
              ),
              (selectedAbilities) => setSelectedAbilities(selectedAbilities)
            ),
            'main_user',
            (main_user: boolean) => !main_user && !editingSelf
          ),
        ]),
      ]);

      setFooterElements([
        Button('save'),
        Link(route(FARM_USERS_VIEW, { farmId, id }), 'cancel'),
      ]);
    }
  }, [
    farmUser,
    farmId,
    id,
    farmUserService,
    t,
    setBodyElements,
    setFooterElements,
    abilities,
    authenticatedUser.id,
  ]);

  const submit = useCallback<SubmitType<FarmUser>>(
    (form) => {
      const formData = { ...form, selectedAbilities };
      delete formData.abilities;
      return [
        farmUserService.update(farmId, id, formData),
        () => history.push(route(FARM_USERS_VIEW, { farmId, id })),
      ];
    },
    [id, farmUserService, history, selectedAbilities, farmId]
  );

  return (
    <Form
      mode={'edit'}
      title={farmUser ? farmUser.name : t('pages.farm.users.edit-user')}
      backTo={null}
      body={bodyElements}
      footer={footerElements}
      values={farmUser}
      submit={submit}
      schema={userSchema}
      noticeAccessor="EditForm"
    />
  );
}
